import { LANGUAGES, type LanguageData } from "@/constants";
import { useTranslation } from "@/hooks/useTranslation";
import { languageAtom } from "@/store/language";
import {
	Button,
	Icon,
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@st-marketplace/ui";
import { useAtom } from "jotai";
import { type FC, useState } from "react";

interface LanguageSelectorProps {
	compact?: boolean;
}

const LanguageSelector: FC<LanguageSelectorProps> = ({ compact = false }) => {
	const { t } = useTranslation();
	const [currentLang, setCurrentLang] = useAtom(languageAtom);
	const [open, setOpen] = useState(false);

	const handleLanguageChange = (lang: LanguageData) => {
		setCurrentLang(lang.code);
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger>
				{compact ? (
					<Button
						size="md"
						color="ghost"
						startIcon={
							<img
								src={LANGUAGES[currentLang].flag}
								alt=""
								className="size-5 rounded-full object-cover"
							/>
						}
						className="rounded-lg"
						aria-label={t("language.changeLanguage", {
							language: LANGUAGES[currentLang].name,
						})}
					>
						{LANGUAGES[currentLang].name}
					</Button>
				) : (
					<Button
						size="lg"
						color="ghost"
						startIcon={
							<img
								src={LANGUAGES[currentLang].flag}
								alt={LANGUAGES[currentLang].name}
								className="size-4 rounded-full object-cover"
							/>
						}
						endIcon={<Icon name="arrow-down-s-line" />}
						className="rounded-full"
					>
						{LANGUAGES[currentLang].name}
					</Button>
				)}
			</PopoverTrigger>

			<PopoverContent align="center" className="w-44 p-1">
				{Object.values(LANGUAGES).map((lang) => (
					<Button
						key={lang.code}
						variant="text"
						type="button"
						onClick={() => handleLanguageChange(lang)}
						color={lang.code === currentLang ? "primary" : "ghost"}
						className="w-full justify-start gap-2 mb-1"
						startIcon={
							<img
								src={lang.flag}
								alt={lang.name}
								className="size-6 rounded-full object-cover"
							/>
						}
					>
						{lang.name}
					</Button>
				))}
			</PopoverContent>
		</Popover>
	);
};

export default LanguageSelector;
